import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, MapPin, Search, X } from "lucide-react";
import {
  FEATURED_CITIES,
  localeCountrySuggestion,
  WORLD_COUNTRIES,
} from "@/data/locations";

const control =
  "min-h-12 w-full rounded-xl border border-dir-gold/20 bg-dir-elevated px-4 py-3 text-sm text-dir-text outline-none transition hover:border-dir-gold/40 focus:border-dir-gold focus:ring-2 focus:ring-dir-gold/15";

export default function SignupLocationFields({ value, onChange }) {
  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    if (value.country) return;
    const detected = localeCountrySuggestion();
    if (detected) setSuggestion(detected);
  }, [value.country]);
  const setCountry = (country) => {
    onChange((prev) => ({
      ...prev,
      country: country?.name || "",
      country_code: country?.code || "",
      city: "",
      location_source: "manual",
    }));
    setSuggestion(null);
  };
  const cityItems = useMemo(
    () => [
      ...(FEATURED_CITIES[value.country] || []).map((name) => ({ name })),
      { name: "Other / Enter city manually" },
    ],
    [value.country],
  );
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <SearchSelect
          label="Country"
          testId="signup-country"
          placeholder="Select country"
          items={WORLD_COUNTRIES}
          value={value.country}
          onSelect={setCountry}
        />
        {suggestion && (
          <button
            type="button"
            onClick={() => setCountry(suggestion)}
            className="mt-2 text-left text-xs text-dir-muted hover:text-dir-gold"
          >
            Suggested from your device:{" "}
            <span className="font-semibold text-dir-gold">
              {suggestion.name}
            </span>
          </button>
        )}
      </div>
      <CityPicker
        country={value.country}
        items={cityItems}
        value={value.city}
        onChange={(city) => onChange((prev) => ({ ...prev, city }))}
      />
    </div>
  );
}

export function CountryPicker({
  value,
  onChange,
  label = "Country",
  testId = "profile-country",
}) {
  return (
    <SearchSelect
      label={label}
      testId={testId}
      placeholder="Select country"
      items={WORLD_COUNTRIES}
      value={value}
      onSelect={(item) => onChange(item)}
    />
  );
}

export function CityPicker({
  country,
  items,
  value,
  onChange,
  testId = "signup-city",
}) {
  const [manual, setManual] = useState(
    !items.some((x) => x.name === value) && !!value,
  );
  if (manual)
    return (
      <div>
        <label className="mb-1.5 block text-sm text-dir-muted">City</label>
        <div className="flex gap-2">
          <input
            autoFocus
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your city"
            className={control}
          />
          <button
            type="button"
            onClick={() => {
              setManual(false);
              onChange("");
            }}
            aria-label="Choose from city list"
            className="min-h-12 rounded-xl border border-dir-gold/20 px-3 text-dir-gold"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  return (
    <SearchSelect
      label="City"
      testId={testId}
      placeholder={country ? "Select city" : "Choose country first"}
      items={items}
      value={value}
      disabled={!country}
      onSelect={(item) => {
        if (item.name.startsWith("Other /")) {
          setManual(true);
          onChange("");
        } else onChange(item.name);
      }}
    />
  );
}

export function SearchSelect({
  label,
  testId,
  placeholder,
  items,
  value,
  onSelect,
  disabled = false,
}) {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(""),
    [active, setActive] = useState(0);
  const root = useRef(null),
    input = useRef(null);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(
        (item) =>
          !q ||
          `${item.name} ${(item.aliases || []).join(" ")}`
            .toLowerCase()
            .includes(q),
      );
  }, [items, query]);
  useEffect(() => {
    const close = (e) => {
      if (!root.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => {
    if (open) setTimeout(() => input.current?.focus(), 30);
  }, [open]);
  const choose = (item) => {
    onSelect(item);
    setOpen(false);
    setQuery("");
    setActive(0);
  };
  return (
    <div ref={root} className="relative min-w-0">
      <label className="mb-1.5 block text-sm text-dir-muted">{label}</label>
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={() => setOpen((x) => !x)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${control} flex items-center justify-between gap-3 text-left disabled:cursor-not-allowed disabled:opacity-50`}
      >
        <span
          className={`truncate ${value ? "text-dir-text" : "text-dir-faint"}`}
        >
          {value || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-dir-gold transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="fixed inset-x-3 bottom-3 z-[100] max-h-[min(72vh,560px)] overflow-hidden rounded-2xl border border-dir-gold/25 bg-dir-deep p-3 shadow-2xl sm:absolute sm:inset-x-auto sm:bottom-auto sm:top-full sm:mt-2 sm:w-full sm:min-w-[300px]">
          <div className="mb-2 flex items-center justify-between sm:hidden">
            <span className="text-sm font-semibold text-dir-text">
              Select {label}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={`Close ${label} picker`}
              className="flex h-11 w-11 items-center justify-center rounded-full text-dir-gold"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="sticky top-0 z-10 bg-dir-deep pb-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dir-gold" />
              <input
                ref={input}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActive((i) => Math.min(i + 1, shown.length - 1));
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActive((i) => Math.max(i - 1, 0));
                  }
                  if (e.key === "Enter" && shown[active]) {
                    e.preventDefault();
                    choose(shown[active]);
                  }
                  if (e.key === "Escape") setOpen(false);
                }}
                placeholder={`Search ${label.toLowerCase()}...`}
                className={`${control} pl-10 pr-10`}
              />
            </div>
          </div>
          <div
            role="listbox"
            className="max-h-[calc(min(72vh,560px)-84px)] overflow-y-auto overscroll-contain pr-1"
          >
            {shown.map((item, index) => {
              const letter = item.pinned
                  ? "Pinned"
                  : item.name[0].toUpperCase(),
                previous = index ? shown[index - 1] : null,
                showGroup =
                  !previous ||
                  item.pinned !== previous.pinned ||
                  (!item.pinned && letter !== previous.name[0].toUpperCase());
              return (
                <div key={`${item.code || item.name}-${index}`}>
                  {showGroup && (
                    <div className="sticky top-0 bg-dir-deep/95 px-3 py-2 text-[10px] font-bold uppercase tracking-[.18em] text-dir-gold">
                      {letter}
                    </div>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={item.name === value}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => choose(item)}
                    className={`flex min-h-11 w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm ${index === active || item.name === value ? "bg-dir-gold/12 text-dir-gold" : "text-dir-muted hover:bg-dir-elevated hover:text-dir-text"}`}
                  >
                    <span>{item.name}</span>
                    {item.name === value && <Check className="h-4 w-4" />}
                  </button>
                </div>
              );
            })}
            {!shown.length && (
              <p className="p-5 text-center text-sm text-dir-faint">
                No matching {label.toLowerCase()} found.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
